'use client';

import {
  FC,
  PropsWithChildren,
  createContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  IChipsState,
  IFiltersContext,
  IFiltersProvider,
} from 'organisms/Filters/types';
import { useTranslations } from 'modules/i18n/useTranslations';
import { SortOptions, SortOrder } from 'commerce/factFinder/queryParameters';

export const FiltersContext = createContext<IFiltersContext>({
  facets: [],
  filters: [],
  sortOptions: [],
  fss: '',
  chipsState: null,
  hasChanged: false,
  labelMap: {},
  sortBy: {
    field: SortOptions.relevancy,
    direction: SortOrder.desc,
  },
  setSortBy: () => {},
  addSingleFilter: () => {},
  addMultipleFilter: () => {},
  toggleChips: () => {},
  setFilters: () => {},
  setChipsState: () => {},
  handleSearch: () => {},
  handleReset: () => {},
});

export const FiltersProvider: FC<
  PropsWithChildren & { value: IFiltersProvider }
> = ({ children, value }) => {
  const [chipsState, setChipsState] = useState<IChipsState | null>(null);
  const haChanged = useRef(false);
  const t = useTranslations();
  const labelMap = {
    brand: t('search.filters.brand'),
    type: t('search.filters.type'),
    size: t('search.filters.size'),
    materials: t('search.filters.materials'),
    genders: t('search.filters.genders'),
    tags: t('search.filters.tags'),
    colors: t('search.filters.color'),
  };

  const toggleChips: IFiltersContext['toggleChips'] = (
    filterKey,
    list,
    position = 'bottom',
  ) => {
    setChipsState((prevState) =>
      prevState?.filterKey === filterKey ? null : { filterKey, list, position },
    );
  };

  useEffect(() => {
    if (!haChanged.current) {
      haChanged.current = true;
    }
  }, [value.filters, value.sortBy]);
  /**
   * This is used for filters with multiple values, e.g., brands, colors.
   * Adds a filter item to the existing filters if it is not already present.
   * If the filter item is already present, it removes it.
   *
   * @function
   * @param {FilterParam} item - The filter item to add or remove.
   */
  const addMultipleFilter: IFiltersContext['addMultipleFilter'] = (item) => {
    value?.setFilters?.((prevFilters) => {
      const existingFilter = prevFilters.find(
        (a) => a.value === item.value && a.type === item.type,
      );
      // Remove filter if it was active
      if (existingFilter) {
        return prevFilters.filter((f) => f != existingFilter);
      }
      return [...prevFilters, item];
    });
  };
  /**
   * This is used for filters with a single value, e.g., price.
   * Adds or replaces a filter item in the existing filters.
   * If a filter item with the same type already exists, it replaces it with the new one.
   *
   * @function
   * @param {FilterParam} item - The filter item to add or replace.
   */
  const addSingleFilter: IFiltersContext['addSingleFilter'] = (item) => {
    value?.setFilters?.((prevFilters) => {
      const updatedFilters = prevFilters.filter((f) => f.type !== item.type);
      return [...updatedFilters, item];
    });
  };

  return (
    <FiltersContext.Provider
      value={{
        chipsState,
        setChipsState,
        toggleChips,
        addSingleFilter,
        addMultipleFilter,
        labelMap,
        hasChanged: haChanged.current,
        ...value,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
